import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import {
  EnvironmentVariables,
  GetPersonalMeetingRoomResponse,
} from "@/modules/common/types";
import {
  AddSaleRequest,
  GetLeadResponse,
  GetLeadViewersResponse,
  GetStatusCategoriesResponse,
  MergeLeadRequest,
} from "@/modules/leads/dtos";

import {
  AddDeleteNextActionResponse,
  AddNextActionRequest,
  DeleteCommentResponse,
  GetCommentsResponse,
  LeadCompoundResponse,
  LinkLocationRequest,
  LinkLocationResponse,
  LocationMappingResponse,
  GetNextActionsResponse,
  GetLeadListings,
  LeadSalesResponse,
  CommentsResponse,
} from "@/modules/lead-page/dtos";
import { AxiosRequestConfigMethods } from "@/api/types";
import { LeadSale } from "@/modules/lead-page/types";

export const getLead = async (
  lead_id: number | undefined
): Promise<GetLeadResponse> => {
  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const editLead = async (
  lead_id: string | number | undefined,
  data: { [key: string]: string | number }
): Promise<GetLeadResponse> => {
  if (!lead_id) {
    throw new Error("Lead ID is required");
  }

  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data,
    },
  });
};

export const assignLead = ({
  leadId,
  userId,
  managerId,
}: {
  leadId: number | undefined;
  userId: number;
  managerId: number;
}) => {
  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${leadId}/AssignUser`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
      data: { user_id: userId, manager_id: managerId },
    },
  });
};

export const makeTelephonePrimary = async (
  lead_id: string | number | undefined,
  data: { [key: string]: string | number }
): Promise<GetLeadResponse> => {
  if (!lead_id) {
    throw new Error("Lead ID is required");
  }

  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/make_telephone_primary`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const deleteTelephone = async (
  lead_id: string | number | undefined,
  data: { [key: string]: string | number }
): Promise<GetLeadResponse> => {
  if (!lead_id) {
    throw new Error("Lead ID is required");
  }

  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/delete_telephone`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
      data,
    },
  });
};

export const addTelephone = async (
  lead_id: string | number | undefined,
  data: { [key: string]: string | number }
): Promise<GetLeadResponse> => {
  if (!lead_id) {
    throw new Error("Lead ID is required");
  }

  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/add_telephone`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const addComment = async (data: any): Promise<any> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/comments`
  );

  const formData = new FormData();

  Object.keys(data).forEach((key) => {
    const value = data[key];
    if (Array.isArray(value)) {
      for (const i of value) {
        formData.append(`${key}[]`, i);
      }
    } else {
      formData.append(key, value);
    }
  });

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: formData,
      formData: true,
    },
  });
};

export const getUsersWithAccess = async (
  lead_id: string | number | undefined
): Promise<any> => {
  const apiUrl = brandUrl<GetCommentsResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/get_users_with_access/${lead_id}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getComments = async (
  lead_id: string | number | undefined
): Promise<any> => {
  const apiUrl = brandUrl<CommentsResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/comments/by-lead-journey/${lead_id}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const deleteComments = async (
  comment_id: string | number | undefined
): Promise<any> => {
  const apiUrl = brandUrl<DeleteCommentResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/comments/${comment_id}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const getStatusComments = async (
  lead_id: string | number | undefined
): Promise<any> => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/status_updates`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const getAssignmentComments = async (
  lead_id: string | number | undefined
): Promise<any> => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/${lead_id}/assignments`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const archiveLead = async (lead_id: string | number | undefined) => {
  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/archive`
  );
  return fetchFromBrandedUrl({
    options: { brandedUrl: apiUrl, isEncoded: false, method: "PUT" },
  });
};

export const unArchiveLead = async (lead_id: string | number | undefined) => {
  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/un_archive`
  );
  return fetchFromBrandedUrl({
    options: { brandedUrl: apiUrl, isEncoded: false, method: "PUT" },
  });
};

export const referLeadToEntity = async (
  lead_id: number | undefined,
  entityName: string
) => {
  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/${entityName}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { lead_id },
    },
  });
};

export const mergeLeads = async (
  body: MergeLeadRequest,
  parentLeadId: number
) => {
  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${parentLeadId}/merge_lead`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: body,
    },
  });
};

export const addLeadViewer = async (
  lead_id: number | undefined,
  agent_id: number | undefined
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/lead_viewers`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { user_id: agent_id },
    },
  });
};

export const getLeadViewers = async (lead_id: number | undefined) => {
  const apiUrl = brandUrl<GetLeadViewersResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/lead_viewers`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
    },
  });
};

export const removeLeadViewer = async (
  lead_id: number | undefined,
  agent_id: number | undefined
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/lead_viewers/${agent_id}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const markLeadViewerAsReferrer = async (
  lead_id: number | undefined,
  agent_id: number | undefined
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/lead_viewers/${agent_id}/mark_as_referrer`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
    },
  });
};

export const removeLeadViewerAsReferrer = async (
  lead_id: number | undefined
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/lead_viewers/remove_referral`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
    },
  });
};

// TODO : add correct url
export const reportIssue = async (
  lead_id: string | number | undefined,
  data: any
): Promise<any> => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/issues`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const unmergeLead = async (lead_id: number | string) => {
  const apiUrl = brandUrl<GetLeadResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/unmerge_lead`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      method: AxiosRequestConfigMethods.GET,
      isEncoded: false,
    },
  });
};

export const deleteLead = async (lead_id: number | undefined) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}`
  );
  return fetchFromBrandedUrl({
    options: { brandedUrl: apiUrl, isEncoded: false, method: "DELETE" },
  });
};

export const autoAssignLead = async (lead_id: number | undefined) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/auto_assign`
  );
  return fetchFromBrandedUrl({
    options: { brandedUrl: apiUrl, isEncoded: false, method: "PATCH" },
  });
};

export const getLeadSales = async (lead_id: string | number | undefined) => {
  const apiUrl = brandUrl<LeadSalesResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/sales`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const toggleDoNotCall = async (
  flag: boolean,
  lead_id: number | undefined
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: { do_not_call: flag },
    },
  });
};

export const addSale = async (
  lead_id: string | number | undefined,
  data: AddSaleRequest
): Promise<AddSaleRequest> => {
  if (!lead_id) {
    throw new Error("Lead ID is required");
  }

  const apiUrl = brandUrl<AddSaleRequest>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/sales/`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const getNextActions = async (
  journey_uid: string | number | undefined
): Promise<GetNextActionsResponse> => {
  const apiUrl = brandUrl<GetNextActionsResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/next-actions/by-lead-journey/${journey_uid}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
};

export const addNextActions = async (
  data: AddNextActionRequest
): Promise<AddDeleteNextActionResponse> => {
  const apiUrl = brandUrl<AddDeleteNextActionResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/next-actions`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data,
    },
  });
};

export const deleteNextActions = async (
  next_action_id: string | number | undefined
): Promise<AddDeleteNextActionResponse> => {
  const apiUrl = brandUrl<AddDeleteNextActionResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/next-actions/${next_action_id}`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.DELETE,
    },
  });
};

export const sendPersonalMeetingRoom = async (
  lead_id: number | undefined,
  meeting_timing: string,
  user_id: string | undefined
) => {
  const apiUrl = brandUrl<GetPersonalMeetingRoomResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/zoom_meeting_histories`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: {
        zoom_meeting_history: {
          lead_id: lead_id,
          user_id: user_id,
          meeting_timing: meeting_timing,
        },
      },
    },
  });
};

export const getLeadListings = async (lead_id: string) => {
  const apiUrl = brandUrl<GetLeadListings>(
    `${EnvironmentVariables.CRM_BASE_URL}/listings/${lead_id}`
  );
  return fetchFromBrandedUrl({
    options: { brandedUrl: apiUrl, isEncoded: false, method: "GET" },
  });
};

export const editLeadListing = async (
  listing_id: number | string,
  data: { [key: string]: number | string }
) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/listings/${listing_id}`
  );
  return fetchFromBrandedUrl({
    options: { brandedUrl: apiUrl, isEncoded: false, method: "PUT", data },
  });
};

export const getLeadCompound = async (compound_id: number) => {
  const apiUrl = brandUrl<LeadCompoundResponse>(
    `${EnvironmentVariables.LISTING_API_URL}/compounds/get_compound_for_crm`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { id: compound_id },
    },
  });
};

export const getLocationMapping = async (location_name: string) => {
  const apiUrl = brandUrl<LocationMappingResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/get_mapping`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: { location_name: location_name },
    },
  });
};

export const LinkLocation = async (requestData: LinkLocationRequest) => {
  const apiUrl = brandUrl<LinkLocationResponse>(
    `${EnvironmentVariables.CRM_BASE_URL}/mappings`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: requestData,
    },
  });
};

export const createSale = ({
  lead_id,
  sale,
}: {
  lead_id: number | string;
  sale: FormData;
}) => {
  const apiUrl = brandUrl<LeadSale>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/sales`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "POST",
      data: sale,
      formData: true,
    },
  });
};

export const updateSale = ({
  sale_id,
  sale,
  lead_id,
}: {
  sale_id: number;
  sale: FormData;
  lead_id: number;
}) => {
  const apiUrl = brandUrl<LeadSale>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${lead_id}/sales/${sale_id}`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: "PATCH",
      formData: true,
      data: sale,
    },
  });
};

export const getStatusCategoryById = (
  statusCategories: GetStatusCategoriesResponse[],
  id: number
) => {
  if (id)
    return statusCategories?.find(
      (statusCategory) => statusCategory.id === id
    ) as GetStatusCategoriesResponse;
  return null;
};

export const startCall = (id: number | string) => {
  const apiUrl = brandUrl<any>(
    `${EnvironmentVariables.CRM_BASE_URL}/leads/${id}/start_call`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PUT,
    },
  });
};
